<template>
  <div class="calendar-box">
    <div class="calendar-top">
      <div>
        <slot name="topLeft"></slot>
      </div>
      <div class="flex-box">
        <ant-icon
          v-if="!hasStartTime || (activeYear > startObj?.year || (activeYear === startObj?.year && active > startObj?.month - 1))"
          @click="leftHD"
          type="LeftOutlined"
        ></ant-icon>
        <ant-icon v-else type="LeftOutlined" style="opacity: .4;cursor: no-drop;"></ant-icon>
        <div class="title-center">{{ activeYear }}年 {{ active + 1 }}月</div>
        <ant-icon
          v-if="!hasDeadline || (activeYear < nowObj?.year || (activeYear === nowObj?.year && active < nowObj?.month - 1))"
          @click="rightHD"
          type="RightOutlined"
        ></ant-icon>
        <ant-icon v-else type="RightOutlined" style="opacity: .4;cursor: no-drop;"></ant-icon>
      </div>
      <div class="flex-box">
        <slot name="topRight"></slot>
      </div>
    </div>
    <div class="big-calendar-clr">
      <swiper class="calendar-swiper swiper-no-swiping" @swiper="onSwiper">
        <swiper-slide v-for="(item, index) in dateArr" :key="index">
          <div class="calendar-item">
            <div
              v-for="(cell,idx) in item"
              class="calendar-item-content"
              :class="{ 'noNow': cell.month !== 'now' }"
            >
              {{ cell?.content }}
              <slot name="item" :record="cell"></slot>
            </div>
          </div>
        </swiper-slide>
      </swiper>
    </div>
  </div>
</template>
  
<script lang="ts">
import { defineComponent, watch, onMounted, ref, PropType, toRefs, reactive } from "vue";
import { Swiper, SwiperSlide } from "swiper/vue";
import { Pagination, Autoplay } from "swiper";

/**
 * 大日历
 */
export default defineComponent({
  isGlobal: true,
  name: "BigCalendar",
  props: {
    // 是否有开始日期
    hasStartTime: {
      type: Boolean,
      default: false,
    },
    // 开始时间 
    startObj: {
      type: Object,
      default: {
        year: 2021,
        month: 10
      },
    },
    // 是否有截至的时间
    hasDeadline: {
      type: Boolean,
      default: false,
    },
    activeTime: {
      type: String,
      default: '',
    }
  },
  components: { Swiper, SwiperSlide },
  emits: ["changeRecords","update:activeTime"],
  setup(props, { attrs, slots, emit }) {
    const state = reactive<{
      active: number,
      nowDay: string,
      nowVale: number,
      activeYear: number,
      dateArr: Array<any>,
      nowObj: any
    }>({
      active: 0,
      nowDay: '',                        // 当前日期数字标识
      nowVale: new Date().getTime(),     // 当前时间戳 
      activeYear: 0,                    // 选中的年份
      dateArr: [],                       // 轮播数据
      nowObj: {}
    });


    // 轮播图操作
    let controlledSwiper = reactive<any>({})
    const onSwiper = (swiper) => {
      controlledSwiper = swiper
    };
    // 左点击
    const leftHD = () => {
      if (state.active <= 0) {
        initAll(state.activeYear - 1, 11)
        return
      }
      emit("update:activeTime",`${state.activeYear}-${state.active >= 10 ? state.active : '0' + state.active}`)
      state.active -= 1
      controlledSwiper?.slideTo(state.active)
    }
    // 右点击
    const rightHD = () => {
      if (state.active >= 11) {
        initAll(state.activeYear + 1, 0,)
        return
      }
      state.active += 1;
      const m = state.active + 1;
      emit("update:activeTime",`${state.activeYear}-${m >= 10 ? m : '0' + m}`)
      controlledSwiper?.slideTo(state.active)
    }


    // 对于时间的处理
    const getYearMonthDay = (date) => {
      let year = date.getFullYear();      // 年
      let month = date.getMonth() + 1;   //  月
      let day = date.getDate();          // 日
      let days = new Date(year, month, 0).getDate();     // 当前月 天数 
      let weekFrist = new Date(year, month - 1, 1).getDay() || 7;     // 当前月 第一天 星期几
      let nowWeekDay = new Date().getDay();   // 今天星期几
      return {
        year,
        month,
        day,
        days,
        weekFrist,
        nowWeekDay,
      };
    }
    // 补零
    const timeFormat = (year, month, day) => {
      return `${year}-${month >= 10 ? month : '0' + month}-${day >= 10 ? day : '0' + day}`
    }


    // 创造一个月的内容
    const createDays = (dateInfo) => {
      let arr = [] as Array<any>;
      // 当前月内容
      for (let i = 0; i < dateInfo.days; i++) {
        let day = i + 1;
        arr.push({
          content: i + 1,
          month: "now",
          timeStamp: new Date(dateInfo.year, dateInfo.month - 1, i + 1).getTime(),
          timeNumber: timeFormat(dateInfo.year, dateInfo.month, day)
        });
      }
      // 上个月尾巴
      let lastDays = new Date(dateInfo.year, dateInfo.month - 1, 0).getDate();
      for (let i = 0; i < dateInfo.weekFrist - 1; i++) {
        let day = lastDays - i;
        let month = dateInfo.month - 1
        arr.unshift({
          content: lastDays - i,
          month: "before",
          timeStamp: new Date(dateInfo.year, dateInfo.month - 2, lastDays - i).getTime(),
          timeNumber: timeFormat(dateInfo.year, month, day)
        });
      }
      // 下个月头部
      let sat = 42;
      for (let i = 0; i < sat - (dateInfo.days + dateInfo.weekFrist - 1); i++) {
        let day = i + 1;
        let month = dateInfo.month + 1
        arr.push({
          content: i + 1,
          month: "future",
          date: dateInfo.year + "-" + (dateInfo.month + 1) + "-" + (i + 1),
          timeStamp: new Date(dateInfo.year, dateInfo.month, i + 1).getTime(),
          timeNumber: timeFormat(dateInfo.year, month, day)
        });
      }

      return arr
    }
    // 创造轮播
    const initAll = (year, active) => {
      let all = [] as Array<any>;
      for (let i = 0; i <= 11; i++) {
        let info = getYearMonthDay(new Date(year, i, 1));
        all.push(createDays(info))
      }
      state.dateArr = all;
      state.activeYear = year;
      state.active = active;
      const m = active + 1;
      emit("update:activeTime",`${state.activeYear}-${m >= 10 ? m : '0' + m}`)
      controlledSwiper?.slideTo(active)
    }


    onMounted(async () => {
      const dateInfo = getYearMonthDay(props?.activeTime ? new Date(props?.activeTime) :  new Date());
      state.nowDay = timeFormat(dateInfo.year, dateInfo.month, dateInfo.day);
      state.nowObj = {
        year: dateInfo.year,
        month: dateInfo.month,
      }
      initAll(dateInfo.year, dateInfo.month - 1);
    });

    return { ...toRefs(state), modules: [Pagination, Autoplay], onSwiper, leftHD, rightHD, };
  },
});
</script>
<style lang="less" scoped>
@import "~@/assets/styles/swiper-bundle.css";
.calendar-box {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #ececec;
  .calendar-top {
    height: 44px;
    background: rgba(0, 0, 0, 0.02);
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 20px;
    font-weight: bold;
    color: rgba(0, 0, 0, 0.85);

    :deep(svg) {
      transform: scale(0.8);
    }
    .title-center {
      width: 120px;
      white-space: nowrap;
      margin: 0 24px;
      text-align: center;
    }
  }
}
.flex-box {
  display: flex;
  align-items: center;
  justify-content: center;
}
.calendar-swiper {
  width: 100%;
  display: flex;
  overflow: hidden;
}

.calendar-item {
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: repeat(7, 1fr);
}
.calendar-item-content {
  box-sizing: content-box;
  height: 72px;
  text-align: center;
  font-size: 32rpx;
  color: rgba(0, 0, 0, 0.85);
  position: relative;
  border-top: 1px solid #ececec;
  border-right: 1px solid #ececec;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
}
.noNow {
  color: rgba(0, 0, 0, 0.45);
}
.calendar-item-content:nth-of-type(7n) {
  border-right: 0;
}
</style>
  